import { Injectable } from '@angular/core';
import { WalletModalConfigService } from './wallet-modal-config.service';
import { ChainRepository, SignerRepository } from '../repository';
import { BehaviorSubject, lastValueFrom, Observable, race } from 'rxjs';
import { Chain } from '../model';
import { ChainId, sleep } from '@31third/common';
import { BrowserProvider, JsonRpcSigner } from 'ethers';
import { AppKit, createAppKit } from '@reown/appkit';
import { EthersAdapter } from '@reown/appkit-adapter-ethers';
import { AppKitNetwork } from '@reown/appkit-common';
import { AlertService } from './alert.service';
import { SafeAppProvider } from '@safe-global/safe-apps-provider';
import SafeAppsSDK from '@safe-global/safe-apps-sdk';
import { SafeInfoExtended } from '@safe-global/safe-apps-sdk/src/types';

@Injectable({
  providedIn: 'root',
})
export class WalletModalService {
  /*

  First we check if the UI is rendered as Safe app. If yes we directly connect
  to the Safe. Otherwise, we configure the wallet modal.

   */

  private safeAppsSDK: SafeAppsSDK;
  private safe: SafeInfoExtended;

  private isSafe: BehaviorSubject<boolean | undefined> = new BehaviorSubject<
    boolean | undefined
  >(undefined);

  private modal: AppKit;
  private networks: AppKitNetwork[];

  private signerSubject: BehaviorSubject<JsonRpcSigner | undefined> =
    new BehaviorSubject<JsonRpcSigner | undefined>(undefined);
  private chainIdSubject: BehaviorSubject<ChainId | undefined> =
    new BehaviorSubject<ChainId | undefined>(undefined);

  constructor(
    private readonly walletModalConfigService: WalletModalConfigService,
    private readonly signerRepository: SignerRepository,
    private readonly chainRepository: ChainRepository,
    private readonly alertService: AlertService,
  ) {
    this.safeAppsSDK = new SafeAppsSDK({
      allowedDomains: [/app\.safe\.global/],
      // debug: true,
    });

    this.chainRepository.initialized$.subscribe(async initialized => {
      if (initialized) {
        this.networks = this.walletModalConfigService.getNetworks();

        if (await this.determineIsSafe()) {
          void this.handleSafe();
        } else {
          this.modal = createAppKit({
            adapters: [new EthersAdapter()],
            networks: [this.networks[0], ...this.networks.slice(1)],
            metadata: this.walletModalConfigService.getMetadata(),
            projectId:
              this.walletModalConfigService.getWalletConnectProjectId(),
            features: {
              email: false,
              socials: false,
              swaps: false,
            },
          });

          this.setupProviderSubscription();
        }
      }
    });
  }

  private async determineIsSafe(): Promise<boolean> {
    if (window?.parent === window) {
      return false;
    }

    try {
      const potentialSafe = await lastValueFrom(
        race([this.safeAppsSDK.safe.getInfo(), sleep(1000)]),
      );

      if (potentialSafe) {
        this.safe = potentialSafe;
      }
      this.isSafe.next(!!potentialSafe);
      return !!potentialSafe;
    } catch (e) {
      // empty on purpose
    }
    return false;
  }

  private async handleSafe(): Promise<void> {
    if (this.safe) {
      const newChainId = `0x${this.safe.chainId.toString(16)}` as ChainId;
      if (newChainId !== this.chainIdSubject.getValue()) {
        this.chainIdSubject.next(newChainId);
      }

      const browserProvider = new BrowserProvider(
        new SafeAppProvider(this.safe, this.safeAppsSDK),
      );

      const newSigner = await browserProvider.getSigner(this.safe.safeAddress);
      if (newSigner !== this.signerSubject.getValue()) {
        this.signerSubject.next(newSigner);
      }
    }
  }

  public async connect(): Promise<void> {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async resolve => {
      if (await this.determineIsSafe()) {
        await this.handleSafe();
        resolve();
      } else {
        await this.modal.open();

        this.modal.subscribeState(async state => {
          if (!state.open) {
            resolve();
          }
        });
      }
    });
  }

  private setupProviderSubscription(): void {
    this.modal.subscribeAccount(async state => {
      const walletProvider = this.modal.getWalletProvider();

      if (state.isConnected && walletProvider) {
        let newChainId: ChainId | undefined = undefined;
        const modalChainId = this.modal.getChainId();
        if (modalChainId) {
          newChainId = `0x${modalChainId.toString(16)}` as ChainId;
        }
        if (newChainId !== this.chainIdSubject.getValue()) {
          this.chainIdSubject.next(newChainId);
        }

        // @ts-expect-error These types should be combinable
        const browserProvider = new BrowserProvider(walletProvider);
        const newSigner = await browserProvider.getSigner(state.address);
        if (newSigner !== this.signerSubject.getValue()) {
          this.signerSubject.next(newSigner);
        }
      } else {
        this.signerRepository.store.reset();
        this.chainIdSubject.next(undefined);
      }
    });
  }

  public getIsSafe(): boolean {
    return !!this.isSafe.getValue();
  }

  public getIsSafeObservable(): Observable<boolean | undefined> {
    return this.isSafe.asObservable();
  }

  public getNewSignerObservable(): Observable<JsonRpcSigner | undefined> {
    return this.signerSubject.asObservable();
  }

  public getNewSignerValue(): JsonRpcSigner | undefined {
    return this.signerSubject.getValue();
  }

  public getNewChainIdObservable(): Observable<ChainId | undefined> {
    return this.chainIdSubject.asObservable();
  }

  public async switchNetwork(chain: Chain): Promise<void> {
    const network = this.networks.find(
      network => (network.id = chain.getIdentifierAsDecimal()),
    );
    if (network) {
      this.modal.switchNetwork(network);
    } else {
      this.alertService.showError(`Couldn't switch to ${chain.name}`);
    }
  }

  public async disconnect(): Promise<void> {
    this.signerRepository.store.reset();
  }

  public async add31ThirdTestnet(): Promise<void> {
    // TODO: fix this
    // const signer = await this.connect();
    // if (signer) {
    //   const testnetChain = this.chainRepository.getChainById(
    //     CHAIN_ID.ETHEREUM_CHAIN_31THIRD,
    //   );
    //   if (!testnetChain) {
    //     console.log('Testnet chain not found!');
    //     return Promise.resolve();
    //   }
    //   // this doesn't work with signer
    //   return signer?.provider.request({
    //     method: 'wallet_addEthereumChain',
    //     params: [
    //       {
    //         chainId: testnetChain.identifier,
    //         chainName: testnetChain.name,
    //         nativeCurrency: {
    //           name: 'Ethereum',
    //           symbol: 'ETH',
    //           decimals: 18,
    //         },
    //       },
    //     ],
    //   });
    // }
  }
}
